import { supabase } from '../lib/supabase';

// Retry helper for Supabase cache issues (PGRST205)
const retryWithBackoff = async (fn, maxRetries = 3, initialDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (error) {
      // If it's a cache error and we have retries left, wait and retry
      if (error?.code === 'PGRST205' && attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`Schema cache error, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      // Otherwise, throw the error
      throw error;
    }
  }
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to create user-friendly error messages
const createUserFriendlyError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  if (!error) return { message: defaultMessage };
  
  let message = error?.message || defaultMessage;
  
  // Network errors
  if (error?.code === 'PGRST301' || error?.message?.includes('fetch') || error?.message?.includes('network')) {
    message = 'Connection problem. Please check your internet and try again.';
  }
  // Permission errors
  else if (error?.code === 'PGRST301' || error?.message?.includes('permission') || error?.message?.includes('policy')) {
    message = 'You don\'t have permission to perform this action.';
  }
  // Not found errors
  else if (error?.code === 'PGRST116' || error?.message?.includes('not found')) {
    message = 'The requested resource was not found.';
  }
  // Validation errors
  else if (error?.code === '23505' || error?.message?.includes('duplicate') || error?.message?.includes('unique')) {
    message = 'This information already exists. Please use different values.';
  }
  
  return {
    ...error,
    message
  };
};

// User Profile Operations
export const profileService = {
  async getProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        throw createUserFriendlyError(error, 'Failed to load profile. Please try again.');
      }
      return toCamelCase(data);
    } catch (error) {
      throw createUserFriendlyError(error, 'Failed to load profile. Please try again.');
    }
  },

  async updateProfile(userId, updates) {
    try {
      // Clean and validate updates before converting
      const cleanedUpdates = { ...updates };
      
      // Ensure relationship_status is valid enum value
      if (cleanedUpdates.relationshipStatus) {
        const validStatuses = ['single', 'married', 'divorced', 'separated', 'widowed', 'complicated'];
        if (!validStatuses.includes(cleanedUpdates.relationshipStatus)) {
          cleanedUpdates.relationshipStatus = 'single'; // Default to single if invalid
        }
      }
      
      // Ensure privacy_level is valid enum value (low, medium, high)
      if (cleanedUpdates.privacyLevel !== undefined) {
        const validPrivacyLevels = ['low', 'medium', 'high'];
        if (!validPrivacyLevels.includes(cleanedUpdates.privacyLevel)) {
          cleanedUpdates.privacyLevel = 'medium'; // Default to medium if invalid
        }
      }
      
      // Validate profile_visibility - must be a valid text value
      if (cleanedUpdates.profileVisibility !== undefined) {
        const validVisibility = ['limited', 'anonymous', 'public', 'private'];
        if (!validVisibility.includes(cleanedUpdates.profileVisibility)) {
          // Map common values to valid ones
          if (cleanedUpdates.profileVisibility === 'anonymous') {
            cleanedUpdates.profileVisibility = 'limited';
          } else {
            cleanedUpdates.profileVisibility = 'limited';
          }
        }
      }
      
      // Validate message_access - must be a valid text value
      if (cleanedUpdates.messageAccess !== undefined) {
        const validAccess = ['verified', 'verified-only', 'all', 'none'];
        if (!validAccess.includes(cleanedUpdates.messageAccess)) {
          // Map to valid value
          if (cleanedUpdates.messageAccess === 'verified-only') {
            cleanedUpdates.messageAccess = 'verified';
          } else {
            cleanedUpdates.messageAccess = 'verified';
          }
        }
      }
      
      // Validate blur_intensity
      if (cleanedUpdates.blurIntensity !== undefined) {
        const validBlurIntensity = ['low', 'medium', 'high'];
        if (!validBlurIntensity.includes(cleanedUpdates.blurIntensity)) {
          cleanedUpdates.blurIntensity = 'medium';
        }
      }
      
      // Ensure age is a valid number
      if (cleanedUpdates.age !== undefined && cleanedUpdates.age !== null) {
        const ageNum = parseInt(cleanedUpdates.age);
        if (isNaN(ageNum) || ageNum < 18 || ageNum > 100) {
          throw new Error('Age must be between 18 and 100');
        }
        cleanedUpdates.age = ageNum;
      }
      
      // Ensure interests is an array
      if (cleanedUpdates.interests !== undefined) {
        if (!Array.isArray(cleanedUpdates.interests)) {
          cleanedUpdates.interests = [];
        }
      }
      
      // Ensure photos is an array - only include if not empty
      if (cleanedUpdates.photos !== undefined) {
        if (!Array.isArray(cleanedUpdates.photos)) {
          cleanedUpdates.photos = [];
        }
        // Remove empty photo arrays to avoid issues
        if (cleanedUpdates.photos.length === 0) {
          delete cleanedUpdates.photos;
          delete cleanedUpdates.photosAlt;
          delete cleanedUpdates.primaryPhoto;
          delete cleanedUpdates.primaryPhotoAlt;
        }
      }
      
      // Ensure photosAlt is an array
      if (cleanedUpdates.photosAlt !== undefined) {
        if (!Array.isArray(cleanedUpdates.photosAlt)) {
          cleanedUpdates.photosAlt = [];
        }
      }
      
      // Remove primaryPhoto if it's null or empty
      if (cleanedUpdates.primaryPhoto === null || cleanedUpdates.primaryPhoto === '') {
        delete cleanedUpdates.primaryPhoto;
      }
      
      if (cleanedUpdates.primaryPhotoAlt === null || cleanedUpdates.primaryPhotoAlt === '') {
        delete cleanedUpdates.primaryPhotoAlt;
      }
      
      // Remove undefined and null values that shouldn't be sent
      Object.keys(cleanedUpdates).forEach(key => {
        if (cleanedUpdates[key] === undefined || cleanedUpdates[key] === null) {
          // Only remove if it's not a required field or if it's explicitly null
          if (key !== 'primaryPhoto' && key !== 'primaryPhotoAlt' && key !== 'bio') {
            delete cleanedUpdates[key];
          }
        }
      });
      
      // Convert to snake_case for database
      const snakeUpdates = toSnakeCase(cleanedUpdates);
      
      // Remove any fields that don't exist in the database schema
      const allowedFields = [
        'display_name', 'age', 'city', 'relationship_status', 'bio', 'interests',
        'photos', 'photos_alt', 'primary_photo', 'primary_photo_alt',
        'privacy_level', 'photo_blurred', 'blur_intensity', 'hide_face',
        'profile_visibility', 'message_access', 'show_online_status',
        'appear_in_search', 'show_last_active'
      ];
      
      const filteredUpdates = {};
      Object.keys(snakeUpdates).forEach(key => {
        if (allowedFields.includes(key)) {
          // Ensure arrays are properly formatted
          if (Array.isArray(snakeUpdates[key])) {
            filteredUpdates[key] = snakeUpdates[key];
          } else if (snakeUpdates[key] !== null && snakeUpdates[key] !== undefined) {
            filteredUpdates[key] = snakeUpdates[key];
          }
        }
      });
      
      // Don't send empty arrays - they can cause issues with Supabase
      if (filteredUpdates.photos && Array.isArray(filteredUpdates.photos) && filteredUpdates.photos.length === 0) {
        delete filteredUpdates.photos;
      }
      if (filteredUpdates.photos_alt && Array.isArray(filteredUpdates.photos_alt) && filteredUpdates.photos_alt.length === 0) {
        delete filteredUpdates.photos_alt;
      }
      if (filteredUpdates.interests && Array.isArray(filteredUpdates.interests) && filteredUpdates.interests.length === 0) {
        // Empty interests array is valid, so keep it
      }
      
      // Remove null values
      Object.keys(filteredUpdates).forEach(key => {
        if (filteredUpdates[key] === null) {
          delete filteredUpdates[key];
        }
      });
      
      console.log('Updating profile with:', JSON.stringify(filteredUpdates, null, 2));
      console.log('User ID:', userId);
      
      const { data, error } = await supabase?.from('user_profiles')?.update(filteredUpdates)?.eq('id', userId)?.select()?.single();

      if (error) {
        console.error('Supabase update error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        console.error('Failed update payload:', JSON.stringify(filteredUpdates, null, 2));
        
        // Provide more specific error message
        let errorMessage = 'Failed to update profile. Please try again.';
        if (error.message) {
          errorMessage = `Failed to update profile: ${error.message}`;
        } else if (error.details) {
          errorMessage = `Failed to update profile: ${error.details}`;
        } else if (error.hint) {
          errorMessage = `Failed to update profile: ${error.hint}`;
        }
        
        throw createUserFriendlyError(error, errorMessage);
      }
      
      console.log('Profile update successful:', data);
      return toCamelCase(data);
    } catch (error) {
      console.error('Profile update error:', error);
      throw createUserFriendlyError(error, 'Failed to update profile. Please try again.');
    }
  },

  async searchProfiles(filters = {}) {
    return retryWithBackoff(async () => {
      // Show profiles that opted in to discovery (RLS also requires appear_in_search = true for other users)
      let query = supabase?.from('user_profiles')?.select('*')?.eq('appear_in_search', true);

      // Apply filters
      if (filters?.gender) {
        query = query?.eq('gender', filters?.gender);
      }

      if (filters?.ageMin) {
        query = query?.gte('age', filters?.ageMin);
      }

      if (filters?.ageMax) {
        query = query?.lte('age', filters?.ageMax);
      }

      if (filters?.city) {
        query = query?.eq('city', filters?.city);
      }

      if (filters?.relationshipStatus && filters?.relationshipStatus !== 'all') {
        query = query?.eq('relationship_status', filters?.relationshipStatus);
      }

      if (filters?.onlineOnly) {
        query = query?.eq('is_online', true);
      }

      if (filters?.verification === 'verified') {
        query = query?.eq('is_verified', true);
      }

      // Sorting
      if (filters?.sortBy === 'newest') {
        query = query?.order('created_at', { ascending: false });
      } else if (filters?.sortBy === 'active') {
        query = query?.order('last_active', { ascending: false });
      } else {
        query = query?.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      return toCamelCase(data);
    });
  },

  async checkUsernameAvailability(username) {
    const { data, error } = await supabase?.from('user_profiles')?.select('username')?.eq('username', username)?.maybeSingle();

    if (error) throw error;
    return !data; // Returns true if available
  },

  async updateOnlineStatus(userId, isOnline) {
    const { error } = await supabase?.from('user_profiles')?.update({ 
        is_online: isOnline,
        last_active: new Date()?.toISOString()
      })?.eq('id', userId);

    if (error) throw error;
  }
};

// Verification (Photo Authenticity & Identity)
const VERIFICATION_BUCKET = 'verifications';

export const verificationService = {
  async submitPhotoVerification(userId, file) {
    // Try to upload file to storage (optional - works even if bucket doesn't exist)
    if (file) {
      try {
        const ext = file?.name?.split('.').pop() || 'jpg';
        const path = `${userId}/photo_selfie_${Date.now()}.${ext}`;
        console.log(`Uploading photo verification to: ${path}`);
        const { data: uploadData, error: uploadError } = await supabase?.storage?.from(VERIFICATION_BUCKET)?.upload(path, file, {
          cacheControl: '3600',
          upsert: false
        });
        if (uploadError) {
          console.error('Storage upload failed:', uploadError);
          console.error('Upload error details:', {
            message: uploadError.message,
            statusCode: uploadError.statusCode,
            error: uploadError.error
          });
          // Continue - we'll still update the profile
        } else {
          console.log('Photo verification uploaded successfully:', uploadData?.path);
        }
      } catch (e) {
        console.error('Storage upload exception:', e);
        // Continue - we'll still update the profile
      }
    } else {
      console.warn('No file provided for photo verification - status will be set to pending without image');
    }
    
    // Update profile verification status to "pending" (awaiting admin review)
    const { data, error } = await supabase?.from('user_profiles')?.update({
      photo_verification_status: 'pending',
      photo_verified_at: new Date()?.toISOString()
      // Keep photo_verified for backward compatibility, but status is what matters
    })?.eq('id', userId)?.select()?.single();
    
    if (error) {
      console.error('Photo verification update error:', error);
      throw createUserFriendlyError(error, 'Failed to submit verification. Please try again.');
    }
    
    return toCamelCase(data);
  },

  async submitIdentityVerification(userId, idFile, selfieFile) {
    // Try to upload files to storage (optional - works even if bucket doesn't exist)
    if (idFile || selfieFile) {
      try {
        if (idFile) {
          const ext = idFile?.name?.split('.').pop() || 'jpg';
          const path = `${userId}/id_doc_${Date.now()}.${ext}`;
          console.log(`Uploading ID document to: ${path}`);
          const { data: uploadData, error: uploadError } = await supabase?.storage?.from(VERIFICATION_BUCKET)?.upload(path, idFile, { cacheControl: '3600', upsert: false });
          if (uploadError) {
            console.error('ID document upload failed:', uploadError);
          } else {
            console.log('ID document uploaded successfully:', uploadData?.path);
          }
        }
        if (selfieFile) {
          const ext = selfieFile?.name?.split('.').pop() || 'jpg';
          const path = `${userId}/id_selfie_${Date.now()}.${ext}`;
          console.log(`Uploading ID selfie to: ${path}`);
          const { data: uploadData, error: uploadError } = await supabase?.storage?.from(VERIFICATION_BUCKET)?.upload(path, selfieFile, { cacheControl: '3600', upsert: false });
          if (uploadError) {
            console.error('ID selfie upload failed:', uploadError);
          } else {
            console.log('ID selfie uploaded successfully:', uploadData?.path);
          }
        }
      } catch (e) {
        console.error('Storage upload exception:', e);
        // Continue - we'll still update the profile
      }
    } else {
      console.warn('No files provided for identity verification - status will be set to pending without images');
    }
    
    // Update profile verification status to "pending" (awaiting admin review)
    const { data, error } = await supabase?.from('user_profiles')?.update({
      identity_verification_status: 'pending',
      identity_verified_at: new Date()?.toISOString()
      // Keep identity_verified for backward compatibility, but status is what matters
    })?.eq('id', userId)?.select()?.single();
    
    if (error) {
      console.error('Identity verification update error:', error);
      throw createUserFriendlyError(error, 'Failed to submit verification. Please try again.');
    }
    
    return toCamelCase(data);
  },

  // Admin functions for moderation
  async getPendingVerifications() {
    return retryWithBackoff(async () => {
      // Get users with pending photo or identity verifications
      // Order by most recent submission date (photo_verified_at or identity_verified_at)
      const { data, error } = await supabase?.from('user_profiles')
        ?.select('id, email, display_name, username, photo_verification_status, photo_verified_at, identity_verification_status, identity_verified_at, created_at')
        ?.or('photo_verification_status.eq.pending,identity_verification_status.eq.pending');
      
      if (error) throw error;
      
      const profiles = toCamelCase(data || []);
      
      // Fetch images from storage for each profile
      const profilesWithImages = await Promise.all(
        profiles.map(async (profile) => {
          const images = {
            photoSelfie: null,
            idDocument: null,
            idSelfie: null
          };
          
          try {
            // List files in user's verification folder
            const { data: files, error: listError } = await supabase?.storage
              ?.from(VERIFICATION_BUCKET)
              ?.list(profile?.id || '', {
                limit: 100,
                sortBy: { column: 'created_at', order: 'desc' }
              });
            
            if (listError) {
              console.warn(`Storage list error for user ${profile?.id}:`, listError);
              // If bucket doesn't exist, that's OK - images just won't show
              if (listError.message?.includes('Bucket not found') || listError.message?.includes('not found')) {
                console.warn('Verifications bucket does not exist. Create it in Supabase Dashboard → Storage');
              }
            }
            
            if (!listError && files && files.length > 0) {
              console.log(`Found ${files.length} files for user ${profile?.id}:`, files.map(f => f.name));
              // Find most recent photo selfie (sort by name which includes timestamp)
              const photoSelfies = files?.filter(f => f?.name?.startsWith('photo_selfie_'));
              if (photoSelfies.length > 0) {
                const photoSelfie = photoSelfies.sort((a, b) => b.name.localeCompare(a.name))[0];
                const path = `${profile?.id}/${photoSelfie?.name}`;
                // Use signed URL for private bucket (expires in 1 hour)
                const { data: signedUrl, error: urlError } = await supabase?.storage
                  ?.from(VERIFICATION_BUCKET)
                  ?.createSignedUrl(path, 3600);
                if (urlError) {
                  console.warn(`Error creating signed URL for ${path}:`, urlError);
                } else if (signedUrl) {
                  images.photoSelfie = signedUrl.signedUrl;
                  console.log(`Photo selfie URL created for ${profile?.id}`);
                }
              }
              
              // Find most recent ID document
              const idDocs = files?.filter(f => f?.name?.startsWith('id_doc_'));
              if (idDocs.length > 0) {
                const idDoc = idDocs.sort((a, b) => b.name.localeCompare(a.name))[0];
                const path = `${profile?.id}/${idDoc?.name}`;
                const { data: signedUrl, error: urlError } = await supabase?.storage
                  ?.from(VERIFICATION_BUCKET)
                  ?.createSignedUrl(path, 3600);
                if (urlError) {
                  console.warn(`Error creating signed URL for ${path}:`, urlError);
                } else if (signedUrl) {
                  images.idDocument = signedUrl.signedUrl;
                  console.log(`ID document URL created for ${profile?.id}`);
                }
              }
              
              // Find most recent ID selfie
              const idSelfies = files?.filter(f => f?.name?.startsWith('id_selfie_'));
              if (idSelfies.length > 0) {
                const idSelfie = idSelfies.sort((a, b) => b.name.localeCompare(a.name))[0];
                const path = `${profile?.id}/${idSelfie?.name}`;
                const { data: signedUrl, error: urlError } = await supabase?.storage
                  ?.from(VERIFICATION_BUCKET)
                  ?.createSignedUrl(path, 3600);
                if (urlError) {
                  console.warn(`Error creating signed URL for ${path}:`, urlError);
                } else if (signedUrl) {
                  images.idSelfie = signedUrl.signedUrl;
                  console.log(`ID selfie URL created for ${profile?.id}`);
                }
              }
            }
          } catch (e) {
            console.warn('Error fetching verification images:', e);
          }
          
          return { ...profile, verificationImages: images };
        })
      );
      
      return profilesWithImages;
    });
  },

  async approvePhotoVerification(userId, adminUserId) {
    const { data, error } = await supabase?.from('user_profiles')?.update({
      photo_verification_status: 'approved',
      photo_verified: true,
      photo_verification_reviewed_at: new Date()?.toISOString(),
      photo_verification_reviewed_by: adminUserId
    })?.eq('id', userId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async rejectPhotoVerification(userId, adminUserId, rejectionReason = null) {
    const updateData = {
      photo_verification_status: 'rejected',
      photo_verified: false,
      photo_verification_reviewed_at: new Date()?.toISOString(),
      photo_verification_reviewed_by: adminUserId
    };
    
    if (rejectionReason) {
      updateData.photo_verification_rejection_reason = rejectionReason;
    }
    
    const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async approveIdentityVerification(userId, adminUserId) {
    const { data, error } = await supabase?.from('user_profiles')?.update({
      identity_verification_status: 'approved',
      identity_verified: true,
      identity_verification_reviewed_at: new Date()?.toISOString(),
      identity_verification_reviewed_by: adminUserId
    })?.eq('id', userId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  },

  async rejectIdentityVerification(userId, adminUserId, rejectionReason = null) {
    const updateData = {
      identity_verification_status: 'rejected',
      identity_verified: false,
      identity_verification_reviewed_at: new Date()?.toISOString(),
      identity_verification_reviewed_by: adminUserId
    };
    
    if (rejectionReason) {
      updateData.identity_verification_rejection_reason = rejectionReason;
    }
    
    const { data, error } = await supabase?.from('user_profiles')?.update(updateData)?.eq('id', userId)?.select()?.single();
    
    if (error) throw error;
    return toCamelCase(data);
  }
};

// Likes/Matches Operations
export const likesService = {
  async createLike(likerId, likedId) {
    // Check if already liked
    const existing = await this.checkIfLiked(likerId, likedId);
    if (existing) {
      return { alreadyLiked: true };
    }

    const { data, error } = await supabase?.from('likes')?.insert({
        liker_id: likerId,
        liked_id: likedId,
        compatibility_score: Math.floor(Math.random() * 30) + 70 // Random 70-100
      })?.select()?.single();

    if (error) throw error;
    
    const likeData = toCamelCase(data);
    
    // Check if it's a mutual match (other user already liked this user)
    // The database trigger should handle this, but we check here too for immediate feedback
    const reverseLike = await this.checkIfLiked(likedId, likerId);
    if (reverseLike) {
      // Update both likes to mutual
      await supabase?.from('likes')?.update({ is_mutual: true })
        ?.or(`and(liker_id.eq.${likerId},liked_id.eq.${likedId}),and(liker_id.eq.${likedId},liked_id.eq.${likerId})`);
      
      likeData.isMutual = true;
    }
    
    return likeData;
  },

  async removeLike(likerId, likedId) {
    const { error } = await supabase?.from('likes')?.delete()?.eq('liker_id', likerId)?.eq('liked_id', likedId);

    if (error) throw error;
  },

  async getMyLikes(userId) {
    const { data, error } = await supabase?.from('likes')?.select(`
        *,
        liked_profile:user_profiles!likes_liked_id_fkey(*)
      `)?.eq('liker_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async getLikesReceived(userId) {
    const { data, error } = await supabase?.from('likes')?.select(`
        *,
        liker_profile:user_profiles!likes_liker_id_fkey(*)
      `)?.eq('liked_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async getMutualMatches(userId) {
    const { data, error } = await supabase?.from('likes')?.select(`
        *,
        liked_profile:user_profiles!likes_liked_id_fkey(*),
        liker_profile:user_profiles!likes_liker_id_fkey(*)
      `)?.eq('is_mutual', true)?.or(`liker_id.eq.${userId},liked_id.eq.${userId}`)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async checkIfLiked(likerId, likedId) {
    const { data, error } = await supabase?.from('likes')?.select('*')?.eq('liker_id', likerId)?.eq('liked_id', likedId)?.maybeSingle();

    if (error) throw error;
    return !!data;
  },

  // Real-time subscription for new matches (mutual likes)
  subscribeToNewMatches(userId, callback) {
    const channel = supabase?.channel(`matches:${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'likes',
          filter: `liked_id=eq.${userId}`
        },
        async (payload) => {
          const newLike = toCamelCase(payload?.new);
          // Check if it's a mutual match
          if (newLike?.isMutual) {
            callback(newLike);
          }
        }
      )?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'likes',
          filter: `liked_id=eq.${userId}`
        },
        async (payload) => {
          const updatedLike = toCamelCase(payload?.new);
          // Check if it became a mutual match
          if (updatedLike?.isMutual) {
            callback(updatedLike);
          }
        }
      )?.subscribe();

    return channel;
  },

  unsubscribeFromMatches(channel) {
    supabase?.removeChannel(channel);
  }
};

// Conversations Operations
export const conversationsService = {
  async getOrCreateConversation(userId, otherUserId) {
    // Check if conversation exists
    const { data: existing, error: searchError } = await supabase?.from('conversations')?.select('*')?.or(`and(participant_one_id.eq.${userId},participant_two_id.eq.${otherUserId}),and(participant_one_id.eq.${otherUserId},participant_two_id.eq.${userId})`)?.maybeSingle();

    if (searchError) throw searchError;
    if (existing) return toCamelCase(existing);

    // Create new conversation
    const { data, error } = await supabase?.from('conversations')?.insert({
        participant_one_id: userId,
        participant_two_id: otherUserId
      })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getMyConversations(userId) {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        participant_one:user_profiles!conversations_participant_one_id_fkey(*),
        participant_two:user_profiles!conversations_participant_two_id_fkey(*)
      `)?.or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)?.order('last_message_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Messages Operations
export const messagesService = {
  async sendMessage(conversationId, senderId, receiverId, messageText, messageType = 'text') {
    try {
      const { data, error } = await supabase?.from('messages')?.insert({
          conversation_id: conversationId,
          sender_id: senderId,
          receiver_id: receiverId,
          message_text: messageText,
          message_type: messageType,
          status: 'sent'
        })?.select()?.single();

      if (error) {
        console.error('Send message error:', error);
        throw createUserFriendlyError(error, 'Failed to send message. Please try again.');
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Send message exception:', error);
      throw error;
    }
  },

  async getConversationMessages(conversationId) {
    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey(*),
        receiver:user_profiles!messages_receiver_id_fkey(*)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

    if (error) throw error;
    return toCamelCase(data);
  },

  async markMessageAsRead(messageId) {
    const { error } = await supabase?.from('messages')?.update({ 
        status: 'read',
        read_at: new Date()?.toISOString()
      })?.eq('id', messageId);

    if (error) throw error;
  },

  async getUnreadCount(userId) {
    const { count, error } = await supabase?.from('messages')?.select('*', { count: 'exact', head: true })?.eq('receiver_id', userId)?.neq('status', 'read');

    if (error) throw error;
    return count || 0;
  },

  // Real-time subscription for new messages
  subscribeToMessages(conversationId, callback) {
    const channel = supabase?.channel(`messages:${conversationId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )?.subscribe();

    return channel;
  },

  unsubscribeFromMessages(channel) {
    supabase?.removeChannel(channel);
  },

  // Real-time subscription for new conversations
  subscribeToNewConversations(userId, callback) {
    const channel = supabase?.channel(`conversations:${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
          filter: `or(participant_one_id.eq.${userId},participant_two_id.eq.${userId})`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )?.subscribe();

    return channel;
  },

  unsubscribeFromConversations(channel) {
    supabase?.removeChannel(channel);
  }
};

// Reports Operations
export const reportsService = {
  async createReport(reporterId, reportedUserId, reason, description) {
    const { data, error } = await supabase?.from('reports')?.insert({
        reporter_id: reporterId,
        reported_user_id: reportedUserId,
        reason,
        description
      })?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getMyReports(userId) {
    const { data, error } = await supabase?.from('reports')?.select(`
        *,
        reported_user:user_profiles!reports_reported_user_id_fkey(*)
      `)?.eq('reporter_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  }
};

// Profile Views Operations
export const profileViewsService = {
  async recordProfileView(viewerId, viewedProfileId) {
    // Don't record if viewing own profile
    if (viewerId === viewedProfileId) return;

    const { error } = await supabase?.from('profile_views')?.insert({
        viewer_id: viewerId,
        viewed_profile_id: viewedProfileId
      });

    if (error && error?.code !== '23505') { // Ignore duplicate key errors
      throw error;
    }
  },

  async getProfileViewsCount(userId) {
    const { count, error } = await supabase?.from('profile_views')?.select('*', { count: 'exact', head: true })?.eq('viewed_profile_id', userId);

    if (error) throw error;
    return count || 0;
  },

  async getRecentViewers(userId, limit = 10) {
    const { data, error } = await supabase?.from('profile_views')?.select(`*,viewer:user_profiles!profile_views_viewer_id_fkey(*)`)?.eq('viewed_profile_id', userId)?.order('viewed_at', { ascending: false })?.limit(limit);

    if (error) throw error;
    return toCamelCase(data);
  },

  // Real-time subscription for profile views
  subscribeToProfileViews(userId, callback) {
    const channel = supabase?.channel(`profile_views:${userId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profile_views',
          filter: `viewed_profile_id=eq.${userId}`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )?.subscribe();

    return channel;
  },

  unsubscribeFromProfileViews(channel) {
    supabase?.removeChannel(channel);
  }
};

// Authentication helper
export const authService = {
  async signUp(email, password, profileData) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: profileData, // This will be stored in raw_user_meta_data
          emailRedirectTo: `${window.location.origin}/registration-login` // Redirect after email confirmation
        }
      });

      // Handle rate limit errors (429)
      if (error) {
        let userFriendlyMessage = error?.message || 'Registration failed. Please try again.';
        
        // Check for rate limit errors
        if (error?.status === 429 || error?.code === '429' || error?.message?.includes('rate limit') || error?.message?.includes('Too Many Requests')) {
          userFriendlyMessage = 'Registration rate limit reached. This is a temporary restriction. Please wait 1-2 hours before trying again, or contact support if you need immediate access. For testing, please use the demo accounts provided on the login page.';
        }
        // Check for email already registered
        else if (error?.message?.includes('already registered') || error?.message?.includes('User already registered')) {
          userFriendlyMessage = 'This email is already registered. Please sign in instead.';
        }
        // Check for invalid email
        else if (error?.message?.includes('Invalid email') || error?.message?.includes('email')) {
          userFriendlyMessage = 'Please enter a valid email address.';
        }
        // Check for weak password
        else if (error?.message?.includes('Password')) {
          userFriendlyMessage = 'Password is too weak. Please use a stronger password.';
        }

        return { 
          data: null, 
          error: { 
            ...error, 
            message: userFriendlyMessage 
          } 
        };
      }

      return { data, error: null };
    } catch (error) {
      // Handle network or unexpected errors
      let errorMessage = 'Network error. Please check your connection and try again.';
      
      if (error?.status === 429 || error?.code === '429') {
        errorMessage = 'Too many registration attempts. Please wait a few minutes before trying again.';
      }

      return { 
        data: null, 
        error: { 
          message: errorMessage,
          status: error?.status,
          code: error?.code
        } 
      };
    }
  },

  async signIn(email, password) {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });

    // Map Supabase errors to user-friendly messages
    if (error) {
      let userFriendlyMessage = error?.message || 'An error occurred during sign in';
      
      if (error?.message?.includes('Invalid login credentials') || error?.message?.includes('Invalid credentials')) {
        userFriendlyMessage = 'Email or password is incorrect';
      } else if (error?.message?.includes('Email not confirmed') || error?.message?.includes('email_not_confirmed')) {
        userFriendlyMessage = 'Please check your email and confirm your account before signing in';
      } else if (error?.message?.includes('User not found') || error?.message?.includes('user_not_found')) {
        userFriendlyMessage = 'No account found with this email. Please create an account first';
      }

      return { 
        data: null, 
        error: { 
          ...error, 
          message: userFriendlyMessage 
        } 
      };
    }

    return { data, error: null };
  },

  async signOut() {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase?.auth?.getUser();
    if (error) throw error;
    return user;
  }
};